import React, { useState } from 'react';
import { ProjectPanel } from '../projects-sheet';
import type { ProjectListItem } from '@/services/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { AlertModal } from '@/components/modal/alert-modal';
import { Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDeleteProject } from '@/services/projects/queries';
import { toast } from 'sonner';

interface CellActionProps {
  data: ProjectListItem;
  activeOrganisationId?: string;
}

export const CellAction: React.FC<CellActionProps> = ({ data, activeOrganisationId }) => {
  const [open, setOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  const isOwnProject = typeof data.organisation === 'object' && data.organisation?.id === activeOrganisationId;

  const { mutate: deleteProject, isPending: isDeleting } = useDeleteProject(
    () => {
      toast.success('Project deleted successfully');
      setShowDeleteModal(false);
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete project');
    }
  );

  const handleEdit = () => {
    navigate(`/dashboard/projects/${data.id}/edit`);
  };

  const handleView = () => {
    setOpen(true);
  };

  const handleDelete = () => {
    deleteProject(data.id);
  };

  // For non-own projects, show simple "View More" link
  if (!isOwnProject) {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className='relative flex w-full flex-col'
      >
        <div
          onClick={handleView}
          id='view-projects'
          className='cursor-pointer text-sm text-primary underline'
        >
          View More
        </div>
        { open && (
          <ProjectPanel
            key={data.id} 
            data={data}
            open={open}
            onOpenChange={setOpen}
          />
        )}
      </div>
    );
  }

  // For own projects, show dropdown with View, Edit, and Delete options
  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            
            <DropdownMenuItem onClick={handleView}>
              <Eye className='mr-2 h-4 w-4' />
              View Details
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className='mr-2 h-4 w-4' />
              Edit Project
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={() => setShowDeleteModal(true)} className='text-red-600'>
              <Trash2 className='mr-2 h-4 w-4' />
              Delete Project
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        { open && (
          <ProjectPanel
            key={data.id} 
            data={data}
            open={open}
            onOpenChange={setOpen}
          />
        )}
      </div>

      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title='Delete Project'
        description='Are you sure you want to delete this project? This action cannot be undone.'
      />
    </>
  );
};
